import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { AdminIdentity } from '@/api/types'

/**
 * Who is signed in.
 *
 * The session itself lives in httpOnly cookies the app cannot read, so "am I signed in" is a
 * question only the server can answer — this query IS the answer, and a 401 from it is what
 * sends the app to the sign-in screen.
 */

export const authKeys = {
  identity: ['auth', 'identity'] as const,
}

export const identityQuery = queryOptions({
  queryKey: authKeys.identity,
  queryFn: ({ signal }) => api.get<AdminIdentity>('/auth/me', undefined, signal),
  // The identity changes only when someone signs in or out, both of which invalidate it
  // explicitly. Re-asking on every window focus would be pure noise.
  staleTime: Infinity,
  // A 401 here is the expected answer for a signed-out visitor, not a failure to retry.
  retry: false,
})

export interface LoginInput {
  readonly email: string
  readonly password: string
}

export function useLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: LoginInput) => api.post<AdminIdentity>('/auth/login', input),
    onSuccess: (identity) => {
      // Seed rather than invalidate: the login response is the identity, so re-fetching it
      // immediately would be a second round trip for data already in hand.
      queryClient.setQueryData(authKeys.identity, identity)
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.post<undefined>('/auth/logout'),
    onSettled: () => {
      /*
       * Cleared on settled, not on success. If the logout request itself fails, the safe
       * assumption is still that this browser should stop showing platform data — the cookies
       * may well be gone, and a panel that keeps rendering after a failed sign-out is worse
       * than one that signs out optimistically.
       */
      queryClient.clear()
    },
  })
}
